import { useState, useEffect, useRef, useCallback, forwardRef, useMemo } from "react";
import { SwivuMenu } from "./SwivuMenu";
import { classNameHelper } from "../../other/utils/utils.jsx";

// Constants
const TRIGGER_BUTTON_CLASS_NAME = "h-8 w-8 text-base cursor-pointer bg-transparent border-none outline-none font-swivu-modern text-muted-foreground hover:text-foreground transition-colors duration-200 rounded-swivu-base hover:bg-secondary flex items-center justify-center disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-muted-foreground";

const INPUT_CLASS_NAME = "w-full p-swivu-input rounded-swivu-base border border-border bg-card text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none transition-all shadow-sm hover:shadow-md border-solid font-swivu-modern text-base cursor-pointer disabled:opacity-50 flex-1 disabled:hover:shadow-sm h-swivu-input";

// Utility functions
const createOptionsMap = (options) => {
  const map = new Map();
  options.forEach(option => {
    if (option.value !== undefined) {
      map.set(option.value, option);
    }
  });
  return map;
};

const filterOptions = (options, searchTerm, hasInput) => {
  if (!hasInput || !searchTerm.trim()) {
    return options;
  }

  const term = searchTerm.toLowerCase();
  return options.filter(option =>
    !option.disabled &&
    (option.label?.toLowerCase().includes(term) ||
     option.value?.toString().toLowerCase().includes(term))
  );
};

const findExactMatch = (options, searchTerm) => {
  if (!searchTerm.trim()) return null;

  const searchTermLower = searchTerm.toLowerCase();
  return options.find(option =>
    !option.disabled &&
    !option.isCreateOption &&
    option.label?.toLowerCase() === searchTermLower
  );
};

const createCreateOption = (searchTerm, createLabel) => ({
  value: `create-${searchTerm}`,
  label: `${createLabel} "${searchTerm}"`,
  icon: "fas fa-plus",
  className: "text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30",
  isCreateOption: true
});

export const SwivuCombo = forwardRef(({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  className = "",
  isCombo = true,
  hasInput = true,
  createLabel = "Create new",
  createOnUnmatched,
  resetOnCreateOnUnmatched = true,
  noResultsLabel = "No results found",
  error,
  type = "text",
  label,
  triggerButtons = [],
  inputCls = "",
  name,
  inputElementClassName = INPUT_CLASS_NAME
}, ref) => {
  // Core state
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [displayValue, setDisplayValue] = useState("");

  // Refs
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const mergedRef = ref || inputRef;
  const pendingSearchClear = useRef(false);

  // Memoized computations
  const optionsList = useMemo(() =>
    typeof options === "function" ? options() : options || [],
    [options]
  );

  const optionsMap = useMemo(() =>
    createOptionsMap(optionsList),
    [optionsList]
  );

  const currentOptions = useMemo(() =>
    filterOptions(optionsList, searchTerm, hasInput),
    [optionsList, searchTerm, hasInput]
  );

  const showCreateOption = useMemo(() =>
    createOnUnmatched && searchTerm.trim() && currentOptions.length === 0,
    [createOnUnmatched, searchTerm, currentOptions.length]
  );

  const extraOptions = useMemo(() =>
    showCreateOption ? [createCreateOption(searchTerm, createLabel)] : [],
    [showCreateOption, searchTerm, createLabel]
  );

  // Effects
  useEffect(() => {
    if (value && optionsMap.has(value)) {
      const selectedOption = optionsMap.get(value);
      setDisplayValue(selectedOption.label || (!isCombo ? value : ""));
    } else if (value && !isCombo) {
      setDisplayValue(value);
    } else {
      setDisplayValue("");
    }
  }, [value, optionsMap, isCombo]);

  useEffect(() => {
    if (isOpen && hasInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, hasInput]);

  // Clear search term after menu closes (prevents showing all options during selection)
  useEffect(() => {
    if (!isOpen && pendingSearchClear.current) {
      setSearchTerm("");
      pendingSearchClear.current = false;
    }
  }, [isOpen]);

  // Event handlers
  const handleExactMatchOnBlur = useCallback(() => {
    if (!searchTerm.trim() || !isCombo) return;

    const exactMatch = findExactMatch(optionsList, searchTerm);

    if (exactMatch) {
      onChange(exactMatch.value);
      setDisplayValue(exactMatch.label);
      pendingSearchClear.current = true;
      setIsOpen(false);
    } else if (showCreateOption) {
      createOnUnmatched?.(searchTerm);
      if (resetOnCreateOnUnmatched) {
        setDisplayValue("");
        pendingSearchClear.current = true;
      }
      setIsOpen(false);
    } else {
      pendingSearchClear.current = true;
      if (isCombo && value && optionsMap.has(value)) {
        setDisplayValue(optionsMap.get(value).label || "");
      }
    }
  }, [searchTerm, isCombo, showCreateOption, createOnUnmatched, resetOnCreateOnUnmatched, optionsList, onChange, value, optionsMap]);

  const handleOptionSelect = useCallback((option) => {
    if (option.disabled) return;

    if (option.isCreateOption) {
      createOnUnmatched?.(searchTerm);
      if (resetOnCreateOnUnmatched) {
        setDisplayValue("");
        pendingSearchClear.current = true;
      }
      setIsOpen(false);
    } else {
      onChange(option.value);
      setDisplayValue(option.label);
      pendingSearchClear.current = true;
      setIsOpen(false);
    }
  }, [onChange, createOnUnmatched, searchTerm, resetOnCreateOnUnmatched]);

  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    setDisplayValue(newValue);
    setSearchTerm(newValue);

    if (!isCombo) {
      onChange(newValue);
    }

    if (!isOpen) {
      setIsOpen(true);
    }
  }, [isCombo, onChange, isOpen]);

  const handleMenuClose = useCallback(() => {
    setIsOpen(false);
    if (isCombo && value && optionsMap.has(value)) {
      setDisplayValue(optionsMap.get(value).label || "");
    }
  }, [isCombo, value, optionsMap]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIsOpen(true);
    }
  }, []);

  const toggleMenu = useCallback(() => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  }, [disabled, isOpen]);

  // Computed values
  const inputClassName = useMemo(() =>
    classNameHelper(
      inputElementClassName,
      {
        "border-border": !error,
        "border-destructive focus:ring-destructive focus:border-destructive": error,
        [inputCls]: inputCls
      },
    ),
    [error, inputCls, inputElementClassName]
  );

  const chevronIconClass = useMemo(() =>
    classNameHelper("text-muted-foreground transition-transform duration-200", {
      "fas fa-chevron-down": !isOpen,
      "fas fa-chevron-up": isOpen
    }),
    [isOpen]
  );

  const buttonTextClass = useMemo(() =>
    classNameHelper({
      "text-foreground": value,
      "text-muted-foreground": !value
    }),
    [value]
  );

  const visibleTriggerButtons = useMemo(() =>
    triggerButtons.filter(button => !button.hidden),
    [triggerButtons]
  );

  return (
    <div ref={containerRef} className={className}>
      {label && (
        <label className="block text-swivu-label font-medium text-foreground mb-swivu-vertical">
          {label}
        </label>
      )}

      <div className="relative flex items-center">
        {/* Input/Button */}
        {hasInput ? (
          <input
            name={name}
            ref={mergedRef}
            type={type}
            value={displayValue}
            onChange={handleInputChange}
            onBlur={handleExactMatchOnBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={placeholder}
            className={inputClassName}
          />
        ) : (
          <button
            type="button"
            disabled={disabled}
            onClick={toggleMenu}
            className={inputClassName}
          >
            <span className={buttonTextClass}>
              {displayValue || placeholder}
            </span>
          </button>
        )}

        {/* Trigger buttons */}
        <div className="flex items-center gap-2 absolute top-0 right-3 h-full">
          {visibleTriggerButtons.map((button, index) => (
            <button
              key={index}
              className={classNameHelper(TRIGGER_BUTTON_CLASS_NAME, button.className)}
              onClick={button.onClick}
              disabled={button.disabled}
              type="button"
              role="button"
            >
              {button.iconCls && <i className={button.iconCls} />}
              {button.label && button.label}
            </button>
          ))}

          {isCombo && (
            <button
              tabIndex={-1}
              className={TRIGGER_BUTTON_CLASS_NAME}
              onClick={toggleMenu}
              disabled={disabled}
              type="button"
              role="button"
            >
              <i className={chevronIconClass} />
            </button>
          )}
        </div>

        {/* Menu */}
        {isCombo && (
          <SwivuMenu
            options={currentOptions}
            extraOptions={extraOptions}
            isOpen={isOpen}
            onItemClick={handleOptionSelect}
            onClose={handleMenuClose}
            noResultsLabel={noResultsLabel}
          />
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="block w-full text-swivu-sm text-destructive font-swivu-modern mt-2">
          {error}
        </div>
      )}
    </div>
  );
});
